import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';

import { DesignLayout } from '@common/DesignLayout';
import { auditStore } from '@stores/auditStore';
import { logsStore } from '@stores/logsStore';

import { AdditionalOptionsDrawer } from './AdditionalOptionsDrawer';
import { StartLogsPageRouteAction } from './constants';
import { EtlTypeFilter } from './EtlTypeFilter';
import { LogsModal } from './LogsModal';
import { ProcessTree } from './ProcessTree';
import { Table } from './Table';

import styles from './Logs.module.scss';

export type IProcess = {
  guid: string;
  reportPackageCode?: string;
  jsonObjects?: { reportVersionCode: string }[];
};

export const Logs = observer(() => {
  const [selectedProcess, setSelectedProcess] = useState<string[]>([]);
  const [process, setProcess] = useState<IProcess | null>(null);
  const [fullSize, toggleFullSize] = useReducer((fullSizeState) => !fullSizeState, false);

  const {
    clear,
    isImportStarted,
    getEtlTypes,
    etlTypes,
    checkActiveProcesses,
    getActiveProcesses,
    setExpandedKeys,
    setFilterValues,
    etlTypeFilter,
  } = logsStore;

  const { state } = useLocation();

  useEffect(() => {
    auditStore.createAuditData({
      moduleName: 'System',
      serviceName: 'Background_job',
      operationName: 'View_monitoring_processes',
      message: `Просмотр раздела "Мониторинг фоновых процессов"`,
    });
  }, []);

  useEffect(() => {
    const initDateRange = {
      dateFrom: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
      dateTo: dayjs().format('YYYY-MM-DD'),
    };

    if (state?.guid) {
      setFilterValues({ ...initDateRange, guid: state.guid });
    }

    if (state?.etlTypeId) {
      const etl = etlTypes.find((item) => item.id === state.etlTypeId);

      if (etl && etl.etlGroup && etl.processName) {
        setExpandedKeys([etl.etlGroup]);
        setSelectedProcess([etl.processName]);
      }
    }

    // позже выпилить, если в контроли добавят возврат etlTypeId
    if (state?.action) {
      const actionByState = {
        [StartLogsPageRouteAction.RUNNING_CONTROLS]: () => {
          setExpandedKeys(['Контроли']);
          setSelectedProcess(['Прогон контролей']);
          setFilterValues(initDateRange);
        },
      };
      actionByState[state.action]?.();
    }
  }, [etlTypes, setExpandedKeys, setFilterValues, setSelectedProcess, state]);

  useEffect(() => {
    getEtlTypes();
    getActiveProcesses();
  }, [getEtlTypes, getActiveProcesses]);

  useEffect(
    () => () => {
      clear();
      setSelectedProcess([]);
    },
    [clear],
  );

  const filterEtlTypes = useMemo(() => {
    if (etlTypeFilter) {
      return etlTypes.filter((type) => type.etlTypeFilter === etlTypeFilter);
    }

    return etlTypes;
  }, [etlTypeFilter, etlTypes]);

  const etlGroups = useMemo(() => Array.from(new Set(filterEtlTypes.map((type) => type.etlGroup))), [filterEtlTypes]);

  const isProcessSelected = !etlGroups.includes(selectedProcess[0]) && selectedProcess.length > 0;

  useEffect(() => {
    let interval;

    if (isImportStarted) {
      interval = setInterval(checkActiveProcesses, 20000);
    }

    if (!isImportStarted) clearInterval(interval);

    return () => clearInterval(interval);
  }, [checkActiveProcesses, isImportStarted]);

  const onClose = () => setProcess(null);

  return (
    <DesignLayout scrollEnable>
      <EtlTypeFilter setSelectedProcess={setSelectedProcess} />

      <div className={styles.container}>
        <ProcessTree
          fullSize={fullSize}
          isProcessSelected={isProcessSelected}
          selectedProcess={selectedProcess}
          setSelectedProcess={setSelectedProcess}
          etl={filterEtlTypes}
        />

        <Table
          fullSize={fullSize}
          isProcessSelected={isProcessSelected}
          selectedProcess={selectedProcess}
          setProcess={setProcess}
          toggleFullSize={toggleFullSize}
        />
      </div>

      <LogsModal open={!!process} onClose={onClose} process={process} />
      <AdditionalOptionsDrawer />
    </DesignLayout>
  );
});
